import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { adminApi } from "../../../lib/admin";
import type {
  AdminGigIssueAdminNoteRequest,
  AdminGigIssueListItemResponse,
  AdminResolveGigIssueRequest,
  AdminUpdateGigIssueRequest,
} from "../../../types/adminTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { COMPANY_EMAIL } from "../../../constants/company";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminIssueDetailModalProps = {
  issue: AdminGigIssueListItemResponse | null;
  isOpen: boolean;
  canWrite: boolean;
  onClose: () => void;
};

const statusTone: Record<NonNullable<AdminGigIssueListItemResponse>["status"], "warning" | "success" | "danger"> = {
  OPEN: "warning",
  UNDER_REVIEW: "warning",
  AWAITING_SETTLEMENT_APPROVAL: "warning",
  RESOLVED_RELEASE_PAYOUT: "success",
  RESOLVED_SETTLEMENT: "success",
  RESOLVED_REFUND_ADULT: "danger",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "danger",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "success",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "success",
};

const statusCopy: Record<NonNullable<AdminGigIssueListItemResponse>["status"], string> = {
  OPEN: "Open",
  UNDER_REVIEW: "Under Review",
  AWAITING_SETTLEMENT_APPROVAL: "Awaiting Settlement Approval",
  RESOLVED_RELEASE_PAYOUT: "Resolved: Payout Released",
  RESOLVED_SETTLEMENT: "Resolved: Settlement Applied",
  RESOLVED_REFUND_ADULT: "Resolved: Refunded",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "Resolved: Refunded Less Fee",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "Resolved: Preparation Payout",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "Resolved: Teen Removed, Funds Held",
};

const issueScopeCopy: Record<NonNullable<AdminGigIssueListItemResponse>["issueScope"], string> = {
  TEEN_ASSIGNMENT: "Teen assignment",
  WHOLE_GIG: "Whole gig",
};

const resolutionCopy: Record<AdminResolveGigIssueRequest["resolution"], string> = {
  RELEASE_PAYOUT: "Release payout",
  SETTLE_HELD_FUNDS: "Request settlement approval",
  APPLY_APPROVED_SETTLEMENT: "Apply approved settlement",
  REFUND_ADULT: "Refund adult",
  REFUND_ADULT_WITH_LATE_FEE: "Refund less $10 fee",
  PAY_TEEN_PREPARATION_AND_REFUND_REST: "Pay teen 3% and refund rest",
  REMOVE_TEEN_KEEP_FUNDED: "Remove teen and keep funded",
};

const scopedResolutionCopy: Record<AdminResolveGigIssueRequest["resolution"], string> = {
  RELEASE_PAYOUT: "Release payout for this teen",
  SETTLE_HELD_FUNDS: "Request settlement for this teen",
  APPLY_APPROVED_SETTLEMENT: "Apply settlement to this teen",
  REFUND_ADULT: "Refund this teen's allocation",
  REFUND_ADULT_WITH_LATE_FEE: "Refund this teen less $10 fee",
  PAY_TEEN_PREPARATION_AND_REFUND_REST: "Pay this teen 3% and refund rest",
  REMOVE_TEEN_KEEP_FUNDED: "Remove this teen and keep funds",
};

const workflowCopy: Record<AdminUpdateGigIssueRequest["action"], string> = {
  MOVE_TO_REVIEW: "Move to review",
};

const defaultSettlement = {
  posterRefundPercent: 100,
  teenPayoutPercent: 0,
  platformKeepPercent: 0,
};

const parsePercentInput = (value: string) => {
  if (value.trim().length === 0) {
    return NaN;
  }
  return Number(value);
};

const AdminIssueDetailModal = ({ issue, isOpen, canWrite, onClose }: AdminIssueDetailModalProps) => {
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState<AdminResolveGigIssueRequest["resolution"]>("RELEASE_PAYOUT");
  const [reason, setReason] = useState("");
  const [posterRefundPercent, setPosterRefundPercent] = useState(String(defaultSettlement.posterRefundPercent));
  const [teenPayoutPercent, setTeenPayoutPercent] = useState(String(defaultSettlement.teenPayoutPercent));
  const [platformKeepPercent, setPlatformKeepPercent] = useState(String(defaultSettlement.platformKeepPercent));
  const [workflowReason, setWorkflowReason] = useState("");
  const [note, setNote] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setResolution("RELEASE_PAYOUT");
      setReason("");
      setPosterRefundPercent(String(defaultSettlement.posterRefundPercent));
      setTeenPayoutPercent(String(defaultSettlement.teenPayoutPercent));
      setPlatformKeepPercent(String(defaultSettlement.platformKeepPercent));
      setWorkflowReason("");
      setNote("");
      setFeedback(null);
    }
  }, [isOpen]);

  const issueDetailQuery = useQuery({
    queryKey: ["admin-issue-detail", issue?.id],
    queryFn: () => adminApi.getIssueDetail(issue!.id),
    enabled: isOpen && issue !== null,
  });

  const detail = issueDetailQuery.data;

  const settlementAllocation = {
    posterRefundPercent: detail?.status === "AWAITING_SETTLEMENT_APPROVAL"
      ? detail.settlementPosterRefundPercent ?? Number.NaN
      : parsePercentInput(posterRefundPercent),
    teenPayoutPercent: detail?.status === "AWAITING_SETTLEMENT_APPROVAL"
      ? detail.settlementTeenPayoutPercent ?? Number.NaN
      : parsePercentInput(teenPayoutPercent),
    platformKeepPercent: detail?.status === "AWAITING_SETTLEMENT_APPROVAL"
      ? detail.settlementPlatformKeepPercent ?? Number.NaN
      : parsePercentInput(platformKeepPercent),
  };

  const isAwaitingSettlementApproval = detail?.status === "AWAITING_SETTLEMENT_APPROVAL";
  const assignmentAllocations = detail?.assignmentAllocations ?? [];
  const hasSiblingAssignments = assignmentAllocations.length > 1;
  const isWholeGigIssue = detail?.issueScope === "WHOLE_GIG";
  const effectiveResolution: AdminResolveGigIssueRequest["resolution"] = isWholeGigIssue && !isAwaitingSettlementApproval
    ? "REMOVE_TEEN_KEEP_FUNDED"
    : resolution;
  const isSettlementResolution = effectiveResolution === "SETTLE_HELD_FUNDS" || effectiveResolution === "APPLY_APPROVED_SETTLEMENT";
  const isSettlementRequest = effectiveResolution === "SETTLE_HELD_FUNDS";
  const settlementSum = settlementAllocation.posterRefundPercent + settlementAllocation.teenPayoutPercent + settlementAllocation.platformKeepPercent;
  const settlementInputsValid = [settlementAllocation.posterRefundPercent, settlementAllocation.teenPayoutPercent, settlementAllocation.platformKeepPercent]
    .every((value) => Number.isInteger(value) && value >= 0 && value <= 100);
  const settlementValid = !isSettlementResolution || (settlementInputsValid && settlementSum === 100);
  const settlementBaseCents = detail?.allocatedPosterChargeCents ?? detail?.totalChargeCents ?? null;
  const resolutionScopeCopy = isWholeGigIssue
    ? "Whole-gig payout, refund, and settlement actions still need per-assignment outcomes. Removing this affected teen keeps the gig funded so the adult can continue with fewer teens or add a replacement."
    : hasSiblingAssignments
      ? `This action affects ${detail?.teenName ?? "the affected teen"} only. ${assignmentAllocations.length - 1} sibling assignment${assignmentAllocations.length - 1 === 1 ? "" : "s"} will not be changed.`
      : "This action affects only the selected teen assignment.";
  const settlementAmounts = settlementBaseCents === null || !settlementInputsValid
    ? null
    : {
        posterRefundCents: Math.floor((settlementBaseCents * settlementAllocation.posterRefundPercent) / 100),
        teenPayoutCents: Math.floor((settlementBaseCents * settlementAllocation.teenPayoutPercent) / 100),
        platformKeepCents:
          settlementBaseCents
          - Math.floor((settlementBaseCents * settlementAllocation.posterRefundPercent) / 100)
          - Math.floor((settlementBaseCents * settlementAllocation.teenPayoutPercent) / 100),
      };

  const resolveMutation = useMutation({
    mutationFn: () => adminApi.resolveIssue(issue!.id, {
      resolution: effectiveResolution,
      reason: reason.trim(),
      posterRefundPercent: isSettlementRequest ? settlementAllocation.posterRefundPercent : undefined,
      teenPayoutPercent: isSettlementRequest ? settlementAllocation.teenPayoutPercent : undefined,
      platformKeepPercent: isSettlementRequest ? settlementAllocation.platformKeepPercent : undefined,
    }),
    onSuccess: async () => {
      setFeedback(
        effectiveResolution === "SETTLE_HELD_FUNDS"
          ? "Settlement approval requested."
          : effectiveResolution === "APPLY_APPROVED_SETTLEMENT"
            ? "Approved settlement applied."
            : `Issue resolved: ${resolutionCopy[effectiveResolution]}.`
      );
      setReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-issues"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-issue-detail", issue?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-payments"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setFeedback(apiErrorToMessage(error, "Unable to resolve issue."));
    },
  });

  const workflowMutation = useMutation({
    mutationFn: (action: AdminUpdateGigIssueRequest["action"]) => adminApi.updateIssueWorkflow(issue!.id, { action, reason: workflowReason.trim() }),
    onSuccess: async (_, action) => {
      setFeedback(`Issue updated: ${workflowCopy[action]}.`);
      setWorkflowReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-issues"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-issue-detail", issue?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setFeedback(apiErrorToMessage(error, "Unable to update issue workflow."));
    },
  });

  const noteMutation = useMutation({
    mutationFn: (payload: AdminGigIssueAdminNoteRequest) => adminApi.addIssueAdminNote(issue!.id, payload),
    onSuccess: async () => {
      setFeedback("Internal note added.");
      setNote("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-issue-detail", issue?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setFeedback(apiErrorToMessage(error, "Unable to add note."));
    },
  });

  const isLateCancellationIssue = detail?.message?.startsWith("Adult late cancellation request.")
    || detail?.message?.startsWith("Teen late cancellation request.")
    || false;
  const canResolve = detail
    ? detail.status === "OPEN" || detail.status === "UNDER_REVIEW" || detail.status === "AWAITING_SETTLEMENT_APPROVAL"
    : false;
  const canMoveToReview = detail?.status === "OPEN";
  const workflowStatusMessage = detail?.status === "UNDER_REVIEW"
    ? "This case is already under review. Use the resolution section below when you're ready to make the final decision."
    : detail?.status === "AWAITING_SETTLEMENT_APPROVAL"
      ? "A settlement request is waiting on team approval. The stored split stays locked until someone applies the approved settlement."
    : detail?.status === "RESOLVED_RELEASE_PAYOUT"
      || detail?.status === "RESOLVED_SETTLEMENT"
      || detail?.status === "RESOLVED_REFUND_ADULT"
      || detail?.status === "RESOLVED_REFUND_ADULT_WITH_LATE_FEE"
      || detail?.status === "RESOLVED_PREPARATION_PAYOUT_AND_REFUND"
      || detail?.status === "RESOLVED_TEEN_REMOVED_KEEP_FUNDED"
      ? "This case has a final resolution. Workflow changes are locked after final resolution."
      : "Move an open case into review while support investigates before making the final payout or refund decision.";

  useEffect(() => {
    if (isOpen && isWholeGigIssue && !isAwaitingSettlementApproval && resolution !== "REMOVE_TEEN_KEEP_FUNDED") {
      setResolution("REMOVE_TEEN_KEEP_FUNDED");
      return;
    }
    if (isOpen && isAwaitingSettlementApproval) {
      setResolution("APPLY_APPROVED_SETTLEMENT");
      return;
    }
    if (isOpen && isLateCancellationIssue && resolution === "RELEASE_PAYOUT") {
      setResolution("SETTLE_HELD_FUNDS");
    }
  }, [isAwaitingSettlementApproval, isLateCancellationIssue, isOpen, isWholeGigIssue, resolution]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={issue ? issue.gigTitle ?? "Gig issue detail" : "Gig issue detail"} panelClassName="max-w-5xl">
      {issueDetailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading issue detail...</p> : null}
      {issueDetailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(issueDetailQuery.error, "Failed to load issue detail.")}</p> : null}

      {detail ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
              <div className="mt-3">
                <AdminPillBadge tone={statusTone[detail.status]}>{statusCopy[detail.status]}</AdminPillBadge>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Reported By</p>
              <div className="mt-3 space-y-2">
                <AdminPillBadge>{detail.reportedByRole}</AdminPillBadge>
                <AdminPillBadge>{issueScopeCopy[detail.issueScope]}</AdminPillBadge>
                <p className="text-sm text-myteengig-text-primary">{detail.reportedByName}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Created</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatDateTime(detail.createdAt)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Resolved</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.resolvedAt ? formatDateTime(detail.resolvedAt) : "Not resolved"}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Issue summary</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p className="text-myteengig-text-primary">{detail.message}</p>
                <p>Gig: {detail.gigTitle ?? "Untitled gig"}</p>
                <p>Reported by email: {detail.reportedByEmail ?? "Not available"}</p>
                <p>Gig request ID: {detail.gigRequestId ?? "Not available"}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Participants</p>
              <div className="mt-3 space-y-3 text-sm text-myteengig-text-muted">
                <div>
                  <p className="font-medium text-myteengig-text-primary">Teen</p>
                  <p>{detail.teenName}</p>
                  <p>{detail.teenEmail ?? "No email available"}</p>
                </div>
                <div>
                  <p className="font-medium text-myteengig-text-primary">Adult</p>
                  <p>{detail.adultName}</p>
                  <p>{detail.adultEmail ?? "No email available"}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Scheduled window</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Start: {formatDateTimeInGigTimeZone(detail.confirmedStartTime, detail.gigTimeZone, "Not confirmed")}</p>
                <p>End: {formatDateTimeInGigTimeZone(detail.confirmedEndTime, detail.gigTimeZone, "Not confirmed")}</p>
                <p>Teen completion: {detail.teenMarkedCompletedAt ? formatDateTime(detail.teenMarkedCompletedAt) : "Not marked"}</p>
                <p>Adult completion: {detail.adultConfirmedCompletedAt ? formatDateTime(detail.adultConfirmedCompletedAt) : "Not marked"}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Payment status</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Status: {detail.paymentStatus ?? "No Stripe payment"}</p>
                <p>Parent checkout total: {detail.totalChargeCents !== null ? formatUsdFromCents(detail.totalChargeCents) : "Not funded"}</p>
                <p>Affected assignment charge: {detail.allocatedPosterChargeCents !== null ? formatUsdFromCents(detail.allocatedPosterChargeCents) : "Not allocated"}</p>
                <p>Affected teen payout: {detail.allocatedTeenPayoutCents !== null ? formatUsdFromCents(detail.allocatedTeenPayoutCents) : "Not allocated"}</p>
                <p>Funded at: {detail.fundedAt ? formatDateTime(detail.fundedAt) : "Not funded"}</p>
                <p>Released at: {detail.releasedAt ? formatDateTime(detail.releasedAt) : "Not released"}</p>
                <p>Refunded at: {detail.refundedAt ? formatDateTime(detail.refundedAt) : "Not refunded"}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Affected assignment</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Assignment ID: {detail.gigFundingAssignmentId ?? "Not funded"}</p>
                <p>Status: {detail.assignmentStatus ?? "No assignment status"}</p>
                <p>Covered seats: {detail.coveredSeats ?? "Not recorded"}</p>
                <p>Allocated fees: {detail.allocatedPlatformFeeCents !== null ? formatUsdFromCents(detail.allocatedPlatformFeeCents) : "Not allocated"}</p>
                <p>Processing fees: {detail.allocatedProcessingFeeCents !== null ? formatUsdFromCents(detail.allocatedProcessingFeeCents) : "Not allocated"}</p>
                <p>Refunded: {detail.refundedAmountCents !== null ? formatUsdFromCents(detail.refundedAmountCents) : "Not refunded"}</p>
                <p>Released: {detail.releasedAmountCents !== null ? formatUsdFromCents(detail.releasedAmountCents) : "Not released"}</p>
                {detail.assignmentRemovedAt ? <p>Removed: {formatDateTime(detail.assignmentRemovedAt)}</p> : null}
                {detail.assignmentRemovalReason ? <p>Removal reason: {detail.assignmentRemovalReason}</p> : null}
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-myteengig-text-primary">Crew context</p>
                <AdminPillBadge>{assignmentAllocations.length} assignments</AdminPillBadge>
              </div>
              {hasSiblingAssignments ? (
                <p className="mt-3 text-sm text-myteengig-text-muted">
                  Resolution controls apply to this teen assignment only. Sibling assignments remain separate.
                </p>
              ) : (
                <p className="mt-3 text-sm text-myteengig-text-muted">
                  No sibling assignments are attached to this checkout.
                </p>
              )}
            </div>
          </div>

          {assignmentAllocations.length > 0 ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">Assignment roster</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    Parent checkout allocation rows for this gig.
                  </p>
                </div>
                <AdminPillBadge>{assignmentAllocations.length} rows</AdminPillBadge>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-myteengig-border text-left text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-myteengig-text-muted">
                      <th className="px-3 py-2 font-semibold">Teen</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Seats</th>
                      <th className="px-3 py-2 font-semibold">Poster charge</th>
                      <th className="px-3 py-2 font-semibold">Teen payout</th>
                      <th className="px-3 py-2 font-semibold">Issue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-myteengig-border">
                    {assignmentAllocations.map((allocation) => {
                      const isAffected = allocation.gigRequestId === detail.gigRequestId;

                      return (
                        <tr key={allocation.assignmentId} className="align-top">
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-myteengig-text-primary">{allocation.teenName}</p>
                              {isAffected ? <AdminPillBadge tone="warning">This issue</AdminPillBadge> : null}
                            </div>
                            <p className="mt-1 text-xs text-myteengig-text-muted">{allocation.teenEmail ?? "No email available"}</p>
                            <p className="mt-1 break-all text-xs text-myteengig-text-muted">Request: {allocation.gigRequestId ?? "Unavailable"}</p>
                          </td>
                          <td className="px-3 py-3">
                            <div className="space-y-2">
                              <AdminPillBadge>{allocation.assignmentStatus}</AdminPillBadge>
                              <p className="text-xs text-myteengig-text-muted">{allocation.paymentStatus}</p>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-myteengig-text-primary">{allocation.coveredSeats}</td>
                          <td className="px-3 py-3 text-myteengig-text-primary">{formatUsdFromCents(allocation.allocatedPosterChargeCents)}</td>
                          <td className="px-3 py-3 text-myteengig-text-primary">{formatUsdFromCents(allocation.allocatedTeenPayoutCents)}</td>
                          <td className="px-3 py-3 text-myteengig-text-muted">
                            <p>{allocation.latestIssueStatus ?? "No issue"}</p>
                            {allocation.latestIssueMessage ? <p className="mt-1 text-xs">{allocation.latestIssueMessage}</p> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Resolution trail</p>
            <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
              <p>Resolved at: {detail.resolvedAt ? formatDateTime(detail.resolvedAt) : "Open"}</p>
              <p>Resolved by admin: {detail.resolvedByAdminUserId ?? "Not resolved"}</p>
              <p>Reason: {detail.resolutionReason ?? "No resolution reason recorded."}</p>
              {detail.settlementRequestedAt ? (
                <p>
                  Settlement requested: {formatDateTime(detail.settlementRequestedAt)}
                  {detail.settlementRequestedByAdminName ? ` by ${detail.settlementRequestedByAdminName}` : ""}
                </p>
              ) : null}
              {detail.settlementRequestReason ? <p>Settlement request note: {detail.settlementRequestReason}</p> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">Internal admin notes</p>
              <AdminPillBadge>{detail.adminNotes.length} notes</AdminPillBadge>
            </div>
            {detail.adminNotes.length === 0 ? (
              <p className="mt-3 text-sm text-myteengig-text-muted">No internal notes yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {detail.adminNotes.map((entry) => (
                  <div key={entry.id} className="rounded-2xl bg-myteengig-background p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-myteengig-text-primary">{entry.adminUserName}</p>
                      <p className="text-xs text-myteengig-text-muted">{formatDateTime(entry.createdAt)}</p>
                    </div>
                    {entry.adminUserEmail ? <p className="mt-1 text-xs text-myteengig-text-muted">{entry.adminUserEmail}</p> : null}
                    <p className="mt-3 whitespace-pre-wrap text-sm text-myteengig-text-primary">{entry.note}</p>
                  </div>
                ))}
              </div>
            )}
            {canWrite ? (
              <div className="mt-4">
                <label htmlFor="admin-issue-note" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                  Add internal note
                </label>
                <textarea
                  id="admin-issue-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                  placeholder="Document outreach, verification steps, or escalation context."
                />
                <div className="mt-3 flex justify-end">
                  <Button
                    variant="outline"
                    disabled={note.trim().length === 0 || noteMutation.isPending}
                    onClick={() => noteMutation.mutate({ note: note.trim() })}
                  >
                    {noteMutation.isPending ? "Saving..." : "Add note"}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Message history</p>
            {detail.messages.length === 0 ? (
              <p className="mt-3 text-sm text-myteengig-text-muted">No in-app messages were recorded for this request.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {detail.messages.map((message) => (
                  <div key={message.id} className="rounded-2xl bg-myteengig-background p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <AdminPillBadge>{message.senderRole}</AdminPillBadge>
                        <p className="text-sm font-semibold text-myteengig-text-primary">{message.senderDisplayName}</p>
                      </div>
                      <p className="text-xs text-myteengig-text-muted">{formatDateTime(message.createdAt)}</p>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-myteengig-text-primary">{message.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Workflow</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {workflowStatusMessage}
            </p>
            {canMoveToReview ? (
              <>
                <label htmlFor="admin-issue-workflow-reason" className="mt-4 mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                  Workflow reason
                </label>
                <textarea
                  id="admin-issue-workflow-reason"
                  value={workflowReason}
                  onChange={(event) => setWorkflowReason(event.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                  placeholder="Support follow-up needed before a final payout decision."
                />
                <div className="mt-4 flex flex-wrap justify-end gap-3">
                  <Button
                    variant="outline"
                    disabled={!canWrite || workflowReason.trim().length === 0 || workflowMutation.isPending}
                    onClick={() => workflowMutation.mutate("MOVE_TO_REVIEW")}
                  >
                    {workflowMutation.isPending ? "Saving..." : "Move to review"}
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Admin resolution</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {isAwaitingSettlementApproval
                ? `This settlement request was already emailed to ${COMPANY_EMAIL}. Once approved, apply the stored split below.`
                : isLateCancellationIssue
                  ? `Choose how to split the held funds across the poster refund, teen payout, and platform keep. Submitting this step emails ${COMPANY_EMAIL} for approval.`
                  : `Choose the final dispute outcome. Settlement requests now email ${COMPANY_EMAIL} first and only run after approval.`}
            </p>
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <AdminPillBadge tone="warning">{isWholeGigIssue ? "Whole gig" : "This teen only"}</AdminPillBadge>
                <p className="text-sm font-semibold text-amber-900">
                  {isWholeGigIssue ? "Whole-gig staffing action" : "Assignment-level action"}
                </p>
              </div>
              <p className="mt-2 text-sm text-amber-900">{resolutionScopeCopy}</p>
              {detail.gigFundingAssignmentId ? (
                <p className="mt-2 break-all text-xs text-amber-800">Affected assignment: {detail.gigFundingAssignmentId}</p>
              ) : null}
            </div>

            {canResolve ? (
              <>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="admin-issue-resolution" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                      Resolution
                    </label>
                    {isAwaitingSettlementApproval ? (
                      <div className="flex h-[50px] items-center rounded-lg border border-myteengig-border bg-myteengig-background/70 px-4 text-sm font-medium text-myteengig-text-primary">
                        Apply approved settlement
                      </div>
                    ) : (
                      <select
                        id="admin-issue-resolution"
                        value={effectiveResolution}
                        onChange={(event) => setResolution(event.target.value as AdminResolveGigIssueRequest["resolution"])}
                        className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                      >
                        {!isWholeGigIssue && !isLateCancellationIssue ? <option value="RELEASE_PAYOUT">Release payout</option> : null}
                        {!isWholeGigIssue ? <option value="SETTLE_HELD_FUNDS">Request settlement approval</option> : null}
                        <option value="REMOVE_TEEN_KEEP_FUNDED">Remove teen and keep funded</option>
                      </select>
                    )}
                  </div>
                  <div>
                    <label htmlFor="admin-issue-resolution-reason" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                      {isAwaitingSettlementApproval ? "Approval note" : "Reason"}
                    </label>
                    <textarea
                      id="admin-issue-resolution-reason"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                      placeholder={isAwaitingSettlementApproval
                        ? "Team approved the requested 90 / 5 / 5 split."
                        : isLateCancellationIssue
                          ? "Late cancellation reviewed. Requesting a 90 / 5 / 5 split for approval."
                          : "Adult no-show confirmed in messages. Refunding the adult in full."}
                    />
                  </div>
                </div>
                {isSettlementResolution ? (
                  <div className="mt-4 rounded-2xl border border-myteengig-border bg-myteengig-background/70 p-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <label htmlFor="admin-issue-poster-refund-percent" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                          Refund to poster %
                        </label>
                        <input
                          id="admin-issue-poster-refund-percent"
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={isAwaitingSettlementApproval ? String(settlementAllocation.posterRefundPercent) : posterRefundPercent}
                          onChange={(event) => setPosterRefundPercent(event.target.value)}
                          disabled={isAwaitingSettlementApproval}
                          className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                        />
                      </div>
                      <div>
                        <label htmlFor="admin-issue-teen-payout-percent" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                          Pay to teen %
                        </label>
                        <input
                          id="admin-issue-teen-payout-percent"
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={isAwaitingSettlementApproval ? String(settlementAllocation.teenPayoutPercent) : teenPayoutPercent}
                          onChange={(event) => setTeenPayoutPercent(event.target.value)}
                          disabled={isAwaitingSettlementApproval}
                          className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                        />
                      </div>
                      <div>
                        <label htmlFor="admin-issue-platform-keep-percent" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                          Keep for platform %
                        </label>
                        <input
                          id="admin-issue-platform-keep-percent"
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={isAwaitingSettlementApproval ? String(settlementAllocation.platformKeepPercent) : platformKeepPercent}
                          onChange={(event) => setPlatformKeepPercent(event.target.value)}
                          disabled={isAwaitingSettlementApproval}
                          className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                        />
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-myteengig-text-muted">
                      {isAwaitingSettlementApproval
                        ? "This split is locked to the proposal already sent for approval."
                        : "The refund, teen payout, and platform keep percentages must add up to 100%."}
                    </p>
                    {!settlementValid ? (
                      <p className="mt-2 text-sm font-medium text-red-600">
                        Enter whole percentages between 0 and 100 that add up to exactly 100.
                      </p>
                    ) : null}
                    {settlementAmounts && settlementBaseCents !== null ? (
                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl border border-myteengig-border bg-white px-4 py-3 md:col-span-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Settlement base</p>
                          <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">{formatUsdFromCents(settlementBaseCents)}</p>
                        </div>
                        <div className="rounded-xl border border-myteengig-border bg-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Poster refund</p>
                          <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">{formatUsdFromCents(settlementAmounts.posterRefundCents)}</p>
                        </div>
                        <div className="rounded-xl border border-myteengig-border bg-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Teen payout</p>
                          <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">{formatUsdFromCents(settlementAmounts.teenPayoutCents)}</p>
                        </div>
                        <div className="rounded-xl border border-myteengig-border bg-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Platform keep</p>
                          <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">{formatUsdFromCents(settlementAmounts.platformKeepCents)}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {feedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{feedback}</p> : null}
                <div className="mt-4 flex justify-end">
                  <Button
                    disabled={!canWrite || reason.trim().length === 0 || !settlementValid || resolveMutation.isPending}
                    onClick={() => resolveMutation.mutate()}
                  >
                    {resolveMutation.isPending ? "Saving..." : scopedResolutionCopy[effectiveResolution]}
                  </Button>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-2xl bg-myteengig-background p-4 text-sm text-myteengig-text-muted">
                This issue is already resolved and its final payout or refund state is shown above.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default AdminIssueDetailModal;
